<template>
    <div>
        <loading :loading="loading" :full-page="false"/>
        <div class="animate-opacity" :style="{'opacity': `${loading?0:1}`}">
            <template v-if="!loading">
                <div v-if="image.type === 'video' && !pickerOnly" class="video-codec">{{ image.codec }} | {{ image.fps }}FPS</div>
                <template v-if="overflowLicence">
                    <div class="licences-container mb-4">
                        <span class="heading">{{ overflowLicence.name }} licence</span>
                    </div>
                    <div class="option-container">
                        <div class="options">
                            <div v-for="price in overflowLicence.prices" :key="price.id" class="option disabled">
                                <div>
                                    <div class="option-name size-name" :class="image.type">
                                        {{ price.name }}
                                    </div>
                                    <div class="size-dimensions">
                                        {{ `${price.width}px x ${price.height}px` }}{{ image.type === 'image' ? ` (${price.dpi}dpi)` : `` }}
                                    </div>
                                    <div class="option-price" :class="image.type">
                                        <a class="download-btn" @click="downloadingStock ? null : download(price.purchaseItemId)">
                                            <b-spinner v-if="downloadingStock"/>
                                            <b-icon-download v-else/>
                                            Download
                                        </a>
                                    </div>
                                </div>
                                <div class="size-dimensions">
                                    {{ `${price.width}px x ${price.height}px` }}{{ image.type === 'image' ? ` (${price.dpi}dpi)` : `` }}
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <div class="licences-container" :style="{'border-color': Object.keys(priceTiers).length > 1 ? priceTierColor : ''}">
                    <template v-if="licences.length > 1">
                        <span class="heading">Pick usage type</span>
                        <b-form-group>
                            <b-form-radio v-for="licence in licences" :key="licence.id" v-model="selected.licence" name="licence-selection" :value="licence.id">{{ licence.name }}</b-form-radio>
                        </b-form-group>
                    </template>
                    <template v-else>
                        <span class="heading">{{ licences[0].name }} licence</span>
                    </template>
                </div>
                <div class="option-container">
                    <span class="heading mt-3">
                        Pick Size
                        <a v-if="image.type === 'image'" v-b-modal.modal-size-guide>Size Guide <span><b-icon-arrow-up-right/></span></a>
                    </span>
                    <div class="options">
                        <div
                            v-for="price in sizesForLicence" :key="price.id" class="option"
                            :class="{selected: (selected.size && (selected.size.name===price.name && selected.size.licence===price.licence)), 'has-discount': hasDiscount && !price.owned, disabled: price.owned}"
                            @click="!price.owned ? selectSize(price) : null"
                        >
                            <span v-if="price.upgrade" class="upgrade-text">Upgrade</span>
                            <div v-if="!price.owned || (price.owned && !pickerOnly)">
                                <div class="option-name size-name" :class="image.type">
                                    {{ price.name }}
                                </div>
                                <div class="size-dimensions">
                                    <template v-if="image.type === 'image'">
                                        <span class="pixel d-block">
                                            {{ price.width }} x {{ price.height }} px | {{ parseFloat((price.width * price.height) / 1000000).toFixed(1) }} MP
                                            <template v-if="price.dpi !== 300">| {{ price.dpi }} dpi</template>
                                        </span>
                                        <span v-if="price.dpi === 300" class="mm d-block">{{ Math.floor((price.width * 25.4) / 300) / 10 }} x {{ Math.floor((price.height * 25.4) / price.dpi) / 10 }} cm | {{ price.dpi }} dpi</span>
                                    </template>
                                    <template v-else>
                                        {{ `${price.width}px x ${price.height}px` }}
                                    </template>
                                </div>
                                <div class="option-price" :class="[image.type, {'has-upgrade':price.upgrade}]">
                                    <template v-if="price.owned">
                                        <a class="download-btn" @click="downloadingStock ? null : download(price.purchaseItemId)">
                                            <b-spinner v-if="downloadingStock"/>
                                            <b-icon-download v-else/>
                                            Download
                                        </a>
                                    </template>
                                    <template v-else>
                                        <span v-if="hasDiscount" class="discount-amt">{{ `${moneyOptionalDecimal(price.price)}` }}</span>
                                        <span>{{ `${moneyOptionalDecimal(hasDiscount ? price.price - (price.price * (discountPcnt / 100)) : price.price)}` }}</span>
                                    </template>
                                </div>
                            </div>
                            <div class="size-dimensions">
                                <template v-if="image.type === 'image'">
                                    <span class="pixel d-block">
                                        {{ price.width }} x {{ price.height }} px | {{ parseFloat((price.width * price.height) / 1000000).toFixed(1) }} MP
                                        <template v-if="price.dpi !== 300">| {{ price.dpi }} dpi</template>
                                    </span>
                                    <span v-if="price.dpi === 300" class="mm d-block">{{ Math.floor((price.width * 25.4) / 300) / 10 }} x {{ Math.floor((price.height * 25.4) / price.dpi) / 10 }} cm | {{ price.dpi }} dpi</span>
                                </template>
                                <template v-else>
                                    {{ `${price.width}px x ${price.height}px` }}
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
                <a v-if="!pickerOnly && image.type !== 'video'" class="prompt-card" @click="showContact('extra-request')">
                    <strong>Looking to go the extra mile?</strong><br>
                    TV usage / Commercial re-print / Exclusive use licence
                    <br>
                    <span class="link">Get a quote ></span>
                </a>
                <template v-if="hasPurchasableSizes && !pickerOnly">
                    <div v-if="selected.licence && selected.size" class="total-container">
                        <div class="total-info">
                            <span class="heading mb-1">Sub Total</span>
                            <p>
                                Global Royalty-Free Licence in perpetuity.<br>
                                {{ image.type.charAt(0).toUpperCase() + image.type.slice(1) }} exclusive to Truestock.<br>
                                No AI used to capture imagery.
                            </p>
                        </div>
                        <div class="total-amt-container">
                            <div class="total-amt">
                                {{ moneyOptionalDecimal(total) }}
                            </div>
                            <div class="total-gst-amt">
                                +{{ moneyOptionalDecimal(gst) }} GST
                            </div>
                        </div>
                    </div>
                    <button v-if="!isInCart || (isInCart && inCartUpdatable)" id="add-to-cart" :disabled="isInCart && !inCartUpdatable" class="btn btn-primary btn-large btn-full-width" @click="addToCart">
                        {{ cartButtonText() }}
                    </button>
                    <nuxt-link v-else class="btn btn-primary btn-large btn-full-width" :to="{name:'cart'}">
                        View in cart
                    </nuxt-link>
                    <nuxt-link v-if="!hasDiscount" :to="{ name: 'pre-purchase', hash: '#prices-section' }" target="_blank" class="prompt-card">
                        <strong>Need a discount?</strong><br>
                        <div class="large">This {{ image.type }} could be <span class="btn-color">{{ moneyOptionalDecimal(total - (total * maxDiscountAvailable)) }} +gst</span></div>
                        <span class="dark"><nobr>Pre-purchase</nobr> credit packs to activate discounts</span>
                        <br>
                        <span class="link">Read more <b-icon-arrow-up-right/></span>
                    </nuxt-link>
                </template>
            </template>
        </div>
        <b-modal id="modal-size-guide" size="md" centered hide-footer>
            <template #modal-title>
                Size Guide
            </template>
            <div class="size-guides">
                <div v-if="locale === 'nz'" class="guide">
                    <span>
                        <span class="size-name">S</span>
                        <span class="size-dimensions">850 pixels wide at <strong>72dpi</strong></span>
                    </span>
                    Great for digital applications like email headers, <br>
                    small web banners, blog posts etc.
                </div>
                <div class="guide">
                    <span>
                        <span class="size-name">M</span>
                        <span class="size-dimensions">1,600 pixels wide at <strong>72dpi</strong></span>
                    </span>
                    Popular size for digital applications like big web banners, <br>
                    PowerPoint presentations, social media posts etc.
                </div>
                <div class="guide">
                    <span>
                        <span class="size-name">L</span>
                        <span class="size-dimensions">2,600 pixels (22cm) wide at <strong>300dpi</strong></span>
                    </span>
                    Versatile size for most online applications and smaller printed <br>
                    applications like annual reports and other marketing collateral.
                </div>
                <div class="guide">
                    <span>
                        <span class="size-name">XL</span>
                        <span class="size-dimensions">At least 5000 pixels (42cm) wide at <strong>300dpi</strong></span>
                    </span>
                    Freedom to print big or small across digital and print.
                </div>
                <div class="guide">
                    <span>
                        <span class="size-name">RAW</span>
                    </span>
                    Uncompressed RAW files available at an extra fee <strong>upon request</strong>
                </div>
            </div>
            <div class="upgrade-blurb">
                <strong>Upgrade to a larger size anytime.</strong>
                Just type the image number (starting with TS) into the search field on the website. If previously purchased, the image price will only show the difference to pay based on upgrade size.<br>
                (you will need to be logged in to your account)
            </div>
        </b-modal>
    </div>
</template>
<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
import Loading from '~/components/global/Loading.vue'

export default {
    name: 'StockPricePicker',
    components: { Loading },
    props: {
        image: {
            type: Object,
            default: null
        },
        loadOnMounted: {
            type: Boolean,
            default: false
        },
        pickerOnly: {
            type: Boolean,
            default: false
        },
        preselectDefault: {
            type: Boolean,
            default: true
        }
    },
    data () {
        return {
            loading: true,
            inCartUpdatable: false,
            overflowLicence: null,
            licences: [
                {}
            ],
            selected: {
                licence: null,
                size: null
            },
            downloadingStock: false,
            hasPurchasableSizes: false,
            sizeDescriptions: {
                s: 'Great for email banners, blog posts & thumbnails',
                m: 'Large digital banners and social media posts',
                l: 'For wide variety of digital and print applications',
                xl: 'Ultimate flexibility for multi-channel campaigns'
            }
        }
    },
    computed: {
        ...mapGetters({
            user: 'auth/user',
            inCart: 'cart/inCart',
            getItem: 'cart/getItem',
            priceTiers: 'data/priceTiers',
            localeVariables: 'data/localeVariables',
            maxPromotion: 'data/maxPromotion',
            creditPacks: 'data/creditPacks'
        }),
        hasDiscount () {
            return ((this.user && this.user.discount_pcnt > 0) || this.maxPromotion())
        },
        discountPcnt () {
            let discountPcntAmt = 0

            if (this.user && this.user.discount_pcnt > 0) {
                discountPcntAmt = this.user.discount_pcnt
            }

            if (this.maxPromotion() && this.maxPromotion().discount_pcnt > discountPcntAmt) {
                discountPcntAmt = this.maxPromotion().discount_pcnt
            }

            return discountPcntAmt
        },
        priceTierColor () {
            let priceTierId = this.image.price_tier_id

            if (this.priceTiers[priceTierId] === undefined || this.priceTiers[priceTierId] === null) {
                priceTierId = 1
            }

            return this.priceTiers[priceTierId].color
        },
        isInCart () {
            return this.inCart(this.image)
        },
        sizesForLicence () {
            const licence = this.hasPurchasableSizes ? this.licences.filter(licence => licence.id === this.selected.licence) : [this.licences[0]]

            for (const i in licence[0].prices) {
                licence[0].prices[i].licence = licence[0].id
            }

            return licence[0].prices
        },
        total () {
            let total = 0

            if (this.hasDiscount) {
                total = parseFloat(this.selected.size.price) - (parseFloat(this.selected.size.price) * (this.discountPcnt / 100))
            } else {
                total = parseFloat(this.selected.size.price)
            }

            return total
        },
        gst () {
            return this.total * (parseFloat(this.localeVariables['gst-pcnt']) / 100)
        },
        maxDiscountAvailable () {
            return (this.creditPacks.reduce((max, item) => Math.max(max, item.discount), 0)) / 100
        }
    },
    watch: {
        user (newUser, oldUser) {
            this.getLicences()
        }
    },
    async mounted () {
        if (this.loadOnMounted) {
            await this.getLicences()
        }

        if (this.isInCart) {
            const cartItem = this.getItem(this.image)

            if (cartItem.size && cartItem.size.licence) {
                this.selected.licence = cartItem.size.licence
                this.selected.size = cartItem.size

                this.$emit('selected', {
                    image: this.image,
                    size: this.selected.size
                })
            }
        }
    },
    methods: {
        getSelected () {
            return {
                image: this.image,
                size: this.selected.size
            }
        },
        async getLicences () {
            this.loading = true

            this.overflowLicence = null
            this.licences = [
                {}
            ]

            await axios.get(`/stock-images/${this.image.code}/licences`)
                .then(rsp => rsp.data)
                .then((response) => {
                    this.licences = Object.values(response)

                    this.checkIfAtLeastOneSizeAvailable()
                    this.setOverflowLicenceIfExists()

                    if (this.hasPurchasableSizes && this.preselectDefault && (!this.selected.licence || !this.selected.size)) {
                        this.preselectSize()
                    } else if (!this.preselectDefault) {
                        this.selected.licence = this.licences[0].id
                    }

                    this.checkUpdatableInCart()
                })
                .finally(() => {
                    this.loading = false
                    this.$emit('loaded')
                })
        },
        checkIfAtLeastOneSizeAvailable () {
            this.licences.forEach((licence) => {
                Object.values(licence.prices).forEach((price) => {
                    if (!price.owned) {
                        this.hasPurchasableSizes = true
                    }
                })
            })
        },
        setOverflowLicenceIfExists () {
            if (this.licences.length > 1 && !this.pickerOnly) {
                const prices = Object.values(this.licences[0].prices)

                if (prices.length === 1 && prices[0].owned === true) {
                    this.overflowLicence = this.licences[0]
                    this.licences.splice(0, 1)
                }
            }
        },
        preselectSize () {
            const firstLicence = this.licences[0]

            let defaultSizeKey = 'l'

            if (this.image.type === 'video') {
                defaultSizeKey = '1080p'
            }

            let defaultSize = Object.values(firstLicence.prices)[0]

            if (Object.hasOwn(firstLicence.prices, defaultSizeKey) && !firstLicence.prices[defaultSizeKey].owned) {
                defaultSize = firstLicence.prices[defaultSizeKey]
            } else {
                Object.values(firstLicence.prices).forEach((price) => {
                    if (!price.owned) {
                        defaultSize = price

                        return false
                    }
                })
            }

            defaultSize.licence = firstLicence.id

            this.selected.licence = firstLicence.id
            this.selected.size = defaultSize

            this.$emit('selected', {
                image: this.image,
                size: this.selected.size
            })

            this.$gtag.event('view_item', {
                currency: this.localeVariables.currency,
                value: parseFloat(this.selected.size.price),
                items: [
                    {
                        item_id: this.imageId,
                        item_name: this.image.description,
                        quantity: 1
                    }
                ]
            })
        },
        download (itemId) {
            this.downloadingStock = true

            axios.get(`/purchase/download/${itemId}`)
                .then(rsp => rsp.data)
                .then((response) => {
                    const link = document.createElement('a')
                    link.href = response
                    document.body.appendChild(link)
                    link.click()
                })
                .finally(() => {
                    this.downloadingStock = false
                })
        },
        cartButtonText () {
            if (!this.isInCart) {
                return `Add ${this.selected.size && this.selected.size.upgrade ? 'upgrade ' : ''}to cart`
            }

            return this.inCartUpdatable ? `Update ${this.selected.size && this.selected.size.upgrade ? 'upgrade ' : ''}in cart` : 'In cart'
        },
        addToCart () {
            const stock = this.image
            stock.licences = this.licences

            if (this.isInCart && this.inCartUpdatable) {
                this.$store.dispatch('cart/updateInCart', {
                    item: {
                        image: this.image,
                        size: { ...this.selected.size }
                    }
                })

                this.$toast.success(`#${this.image.code} updated in cart`)
            } else if (!this.isInCart) {
                this.$store.dispatch('cart/addToCart', {
                    item: {
                        image: this.image,
                        size: { ...this.selected.size }
                    }
                })

                this.$toast.success(`#${this.image.code} added to cart`)
            }

            this.$gtag.event('add_to_cart', {
                currency: this.localeVariables.currency,
                value: parseFloat(this.selected.size.price),
                items: [
                    {
                        item_id: this.imageId,
                        item_name: this.image.description
                    }
                ]
            })

            this.inCartUpdatable = false
        },
        checkUpdatableInCart () {
            if (this.isInCart) {
                const cartItem = this.getItem(this.image)

                if (this.selected.size !== cartItem.size) {
                    this.inCartUpdatable = true
                }
            }
        },
        selectSize (size) {
            this.selected.size = size

            this.checkUpdatableInCart()

            this.$emit('selected', {
                image: this.image,
                size: this.selected.size
            })
        },
        showContact (type) {
            this.$emit('showContact', type)
        }
    }
}
</script>
